/**
 * Payment Service
 * Handles all payment-related API calls to AWS Lambda functions
 */

import {
  getMonthlyCalendarAmountCents,
  getAnnualCalendarAmountCents,
  getNttWeeklyAmountCents,
  getNttMonthlyAmountCents,
  getNttAnnualAmountCents,
  getNtWeeklyAmountCents,
  getNtMonthlyAmountCents,
  getNtAnnualAmountCents
} from '../config/pricing.config';
import { annualCalendarYear } from '../utils/calendarAccess';
import { trafficSource } from '../utils/trafficSource';

const API_BASE_URL = 'https://5hhjpfa808.execute-api.eu-west-1.amazonaws.com/prod';

/**
 * Create a one-time payment link for products
 * @param {Object} params - Payment parameters
 * @param {string} params.product_name - Name of the product
 * @param {number} params.amount_cents - Amount in cents
 * @param {string} params.currency - Currency code (default: 'usd')
 * @param {string} params.customer_email - Customer email
 * @param {Object} params.metadata - Additional metadata
 * @returns {Promise<Object>} Payment session data
 */
export const createPaymentLink = async ({
  product_name,
  amount_cents,
  currency = 'usd',
  customer_email,
  metadata = {}
}) => {
  try {
    const response = await fetch(`${API_BASE_URL}/create-payment-link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        product_name,
        amount_cents,
        currency,
        customer_email,
        // traffic_source: where the buyer came from. Not `source`, which on Stripe metadata means the sales channel ('web')
        metadata: { ...metadata, traffic_source: trafficSource() }
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to create payment link');
    }

    return data;
  } catch (error) {
    console.error('Error creating payment link:', error);
    throw error;
  }
};

/**
 * Create a subscription payment link for memberships
 * @param {Object} params - Subscription parameters
 * @param {string} params.email - Customer email
 * @param {string} params.user_id - User ID
 * @param {string} params.plan_name - Plan name
 * @param {number} params.amount_cents - Amount in cents
 * @param {string} params.currency - Currency code (default: 'usd')
 * @param {string} params.interval - Billing interval ('month' or 'year')
 * @returns {Promise<Object>} Subscription link data
 */
export const createSubscriptionLink = async ({
  email,
  user_id,
  plan_name,
  amount_cents,
  currency = 'usd',
  interval = 'month',
  year,          // annual calendars: the calendar year bought for (the Lambda checks it)
  promo_code = ''
}) => {
  try {
    const response = await fetch(`${API_BASE_URL}/create-subscription-link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        user_id,
        plan_name,
        amount_cents,
        currency,
        interval,
        year,
        promo_code,
        traffic_source: trafficSource()
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to create subscription link');
    }

    return data;
  } catch (error) {
    console.error('Error creating subscription link:', error);
    throw error;
  }
};

/**
 * Reserve a 2027 deep-dive pre-order (no charge). Returns { success, orderId, payLink }.
 * The buyer is emailed their confirmation + payment link; they pay any time (Oct 1 or early).
 */
export const createPreorder = async ({ email, name, bundle, signs }) => {
  const response = await fetch(`${API_BASE_URL}/create-preorder`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // localDate: the server prices the order by the buyer's own date, matching what this page showed
    body: JSON.stringify({ email, name, bundle, signs, localDate: localYmd(), traffic_source: trafficSource() })
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Could not reserve your pre-order. Please try again.');
  }
  return data;
};

/** Today's date in the buyer's own timezone, as YYYY-MM-DD. */
export const localYmd = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * 2027 Deep Dive discount (percent) by the buyer's local date. The order lambda
 * (lambda/ivy-preorder-create.js discountOn) charges the same tiers:
 * until Oct 31 2026 50% (early-bird) · November 30% · December 25% · from Jan 1 2027 full price.
 */
export const getForecastDiscount = (day = localYmd()) =>
  day <= '2026-10-31' ? 50 : day <= '2026-11-30' ? 30 : day <= '2026-12-31' ? 25 : 0;

/** Calendar-code bonus for deep-dive orders placed in September or October 2026. */
export const isCalendarBonusActive = (day = localYmd()) => day >= '2026-09-01' && day <= '2026-10-31';

/**
 * Calculate discounted price in cents
 */
const calculateDiscountedPriceCents = (originalCents) => {
  const discountPercent = getForecastDiscount();
  if (discountPercent === 0) return originalCents;

  const discountedPrice = Math.round(originalCents * (1 - discountPercent / 100));
  return discountedPrice;
};

/**
 * Product pricing configuration with dynamic discounts.
 * amount_cents is a getter so it always uses today's tier, even in a tab opened before midnight.
 */
const forecastBundle = (name, fullCents) => ({
  name,
  original_amount_cents: fullCents,
  get amount_cents() { return calculateDiscountedPriceCents(fullCents); },
  currency: 'eur' // euros from 2026-10-13, same amounts (lambda/ivy-preorder-create.js charges EUR)
});

export const PRODUCTS = {
  SINGLE_RISING: forecastBundle('Single Rising Sign - 2027 Extended Forecast', 3000),   // €30.00 full price
  BIG_THREE_BUNDLE: forecastBundle('Big Three Bundle - 3 Complete 2027 Forecasts', 5000), // €50.00 full price
  ALL_TWELVE_SIGNS: forecastBundle('All 12 Signs Bundle - 2027 Complete Collection', 10000) // €100.00 full price
};

/** "€15.00" / "$9.99": a price in cents with its currency symbol. */
export const formatPrice = (cents, currency = 'usd') =>
  `${String(currency).toLowerCase() === 'eur' ? '€' : '$'}${(cents / 100).toFixed(2)}`;

/**
 * Subscription pricing configuration
 */
export const SUBSCRIPTIONS = {
  MONTHLY_CALENDAR_2026: {
    name: 'Monthly Cosmic Personal Events',
    amount_cents: getMonthlyCalendarAmountCents(), // Auto-updates based on promo status
    currency: 'usd',
    interval: 'month',
    permissions: {
      year_2026: {
        tth_monthly: true,
        tth_annual: false
      }
    }
  },
  ANNUAL_CALENDAR_2026: {
    get name() { return `Annual Cosmic Personal Events - ${annualCalendarYear()}`; }, // sold per calendar year
    get year() { return annualCalendarYear(); },
    amount_cents: getAnnualCalendarAmountCents(), // Auto-updates based on promo status
    currency: 'usd',
    interval: 'year',
    permissions: {
      year_2026: {
        tth_monthly: false,
        tth_annual: true
      }
    }
  },
  NTT_WEEKLY_CALENDAR_2026: {
    name: 'Weekly Personal Cosmic Activation',
    amount_cents: getNttWeeklyAmountCents(), // Auto-updates based on promo status
    currency: 'usd',
    interval: 'week',
    permissions: {
      year_2026: {
        ntt_weekly: true,
        ntt_monthly: false,
        ntt_annual: false
      }
    }
  },
  NTT_MONTHLY_CALENDAR_2026: {
    name: 'Monthly Personal Cosmic Activation',
    amount_cents: getNttMonthlyAmountCents(), // Auto-updates based on promo status
    currency: 'usd',
    interval: 'month',
    permissions: {
      year_2026: {
        ntt_weekly: false,
        ntt_monthly: true,
        ntt_annual: false
      }
    }
  },
  NTT_ANNUAL_CALENDAR_2026: {
    get name() { return `Annual Personal Cosmic Activation - ${annualCalendarYear()}`; }, // sold per calendar year
    get year() { return annualCalendarYear(); },
    amount_cents: getNttAnnualAmountCents(), // Auto-updates based on promo status
    currency: 'usd',
    interval: 'year',
    permissions: {
      year_2026: {
        ntt_weekly: false,
        ntt_monthly: false,
        ntt_annual: true
      }
    }
  },
  NT_WEEKLY_CALENDAR_2026: {
    name: 'Weekly Personal Transit',
    amount_cents: getNtWeeklyAmountCents(), // Auto-updates based on promo status
    currency: 'usd',
    interval: 'week',
    permissions: {
      year_2026: {
        nt_weekly: true,
        nt_monthly: false,
        nt_annual: false
      }
    }
  },
  NT_MONTHLY_CALENDAR_2026: {
    name: 'Monthly Personal Transit',
    amount_cents: getNtMonthlyAmountCents(), // Auto-updates based on promo status
    currency: 'usd',
    interval: 'month',
    permissions: {
      year_2026: {
        nt_weekly: false,
        nt_monthly: true,
        nt_annual: false
      }
    }
  },
  NT_ANNUAL_CALENDAR_2026: {
    get name() { return `Annual Personal Transit - ${annualCalendarYear()}`; }, // sold per calendar year
    get year() { return annualCalendarYear(); },
    amount_cents: getNtAnnualAmountCents(), // Auto-updates based on promo status
    currency: 'usd',
    interval: 'year',
    permissions: {
      year_2026: {
        nt_weekly: false,
        nt_monthly: false,
        nt_annual: true
      }
    }
  },
  MONTHLY_MEMBERSHIP: {
    name: 'Monthly Membership',
    amount_cents: 999, // $9.99/month
    currency: 'usd',
    interval: 'month'
  },
  YEARLY_MEMBERSHIP: {
    name: 'Yearly Membership',
    amount_cents: 9999, // $99.99/year (save 17%)
    currency: 'usd',
    interval: 'year'
  }
};

export default {
  createPaymentLink,
  createPreorder,
  createSubscriptionLink,
  PRODUCTS,
  SUBSCRIPTIONS
};
